/**
 * OWL2 Factory Functions - Consolidated Export
 *
 * Re-exports all factory functions from the types module.
 * Factory functions are also available from their respective type modules.
 */
// IRI factories
export { fullIRI, abbreviatedIRI, parseIRI } from '../types/IRI';
// Literal factories
export { typedLiteral, plainLiteral } from '../types/Literal';
// Entity factories
export { classEntity, datatypeEntity, objectPropertyEntity, dataPropertyEntity, annotationPropertyEntity, namedIndividualEntity, namedIndividual, anonymousIndividual } from '../types/Entity';
// Property expression factories
export { objectProperty, objectInverseOf, dataProperty, objectPropertyChain } from '../types/PropertyExpression';
// Data range factories
export { datatype, dataIntersectionOf, dataUnionOf, dataComplementOf, dataOneOf, facetRestriction, datatypeRestriction } from '../types/DataRange';
// Class expression factories
export { owlClass, objectIntersectionOf, objectUnionOf, objectComplementOf, objectOneOf, objectSomeValuesFrom, objectAllValuesFrom, objectHasValue, objectHasSelf, objectMinCardinality, objectMaxCardinality, objectExactCardinality, dataSomeValuesFrom, dataAllValuesFrom, dataHasValue, dataMinCardinality, dataMaxCardinality, dataExactCardinality } from '../types/ClassExpression';
// Annotation factories
export { annotationProperty, annotation, annotationWithLiteral, annotationWithIRI } from '../types/Annotation';
// Ontology factories
export { prefixDeclaration, importDeclaration, emptyOntology, ontology, addPrefix, addImport, addOntologyAnnotation, addAxiom, addAxioms } from '../types/Ontology';
/**
 * Create a Declaration axiom.
 */
export function declaration(entity, annotations) {
    return {
        axiomType: 'Declaration',
        entity,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a SubClassOf axiom.
 */
export function subClassOf(subClass, superClass, annotations) {
    return {
        axiomType: 'SubClassOf',
        subClass,
        superClass,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an EquivalentClasses axiom.
 */
export function equivalentClasses(first, second, ...rest) {
    return {
        axiomType: 'EquivalentClasses',
        classExpressions: [first, second, ...rest]
    };
}
/**
 * Create a DisjointClasses axiom.
 */
export function disjointClasses(first, second, ...rest) {
    return {
        axiomType: 'DisjointClasses',
        classExpressions: [first, second, ...rest]
    };
}
/**
 * Create a DisjointUnion axiom.
 */
export function disjointUnion(disjointClass, first, second, ...rest) {
    return {
        axiomType: 'DisjointUnion',
        disjointClass,
        classExpressions: [first, second, ...rest]
    };
}
/**
 * Create a SubObjectPropertyOf axiom.
 */
export function subObjectPropertyOf(subProperty, superProperty, annotations) {
    return {
        axiomType: 'SubObjectPropertyOf',
        subProperty,
        superProperty,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an EquivalentObjectProperties axiom.
 */
export function equivalentObjectProperties(first, second, ...rest) {
    return {
        axiomType: 'EquivalentObjectProperties',
        properties: [first, second, ...rest]
    };
}
/**
 * Create a DisjointObjectProperties axiom.
 */
export function disjointObjectProperties(first, second, ...rest) {
    return {
        axiomType: 'DisjointObjectProperties',
        properties: [first, second, ...rest]
    };
}
/**
 * Create an InverseObjectProperties axiom.
 */
export function inverseObjectProperties(property1, property2, annotations) {
    return {
        axiomType: 'InverseObjectProperties',
        property1,
        property2,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an ObjectPropertyDomain axiom.
 */
export function objectPropertyDomainAxiom(property, domain, annotations) {
    return {
        axiomType: 'ObjectPropertyDomain',
        property,
        domain,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an ObjectPropertyRange axiom.
 */
export function objectPropertyRangeAxiom(property, range, annotations) {
    return {
        axiomType: 'ObjectPropertyRange',
        property,
        range,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a FunctionalObjectProperty axiom.
 */
export function functionalObjectProperty(property, annotations) {
    return {
        axiomType: 'FunctionalObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an InverseFunctionalObjectProperty axiom.
 */
export function inverseFunctionalObjectProperty(property, annotations) {
    return {
        axiomType: 'InverseFunctionalObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a ReflexiveObjectProperty axiom.
 */
export function reflexiveObjectProperty(property, annotations) {
    return {
        axiomType: 'ReflexiveObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an IrreflexiveObjectProperty axiom.
 */
export function irreflexiveObjectProperty(property, annotations) {
    return {
        axiomType: 'IrreflexiveObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a SymmetricObjectProperty axiom.
 */
export function symmetricObjectProperty(property, annotations) {
    return {
        axiomType: 'SymmetricObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an AsymmetricObjectProperty axiom.
 */
export function asymmetricObjectProperty(property, annotations) {
    return {
        axiomType: 'AsymmetricObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a TransitiveObjectProperty axiom.
 */
export function transitiveObjectProperty(property, annotations) {
    return {
        axiomType: 'TransitiveObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a SubDataPropertyOf axiom.
 */
export function subDataPropertyOf(subProperty, superProperty, annotations) {
    return {
        axiomType: 'SubDataPropertyOf',
        subProperty,
        superProperty,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an EquivalentDataProperties axiom.
 */
export function equivalentDataProperties(first, second, ...rest) {
    return {
        axiomType: 'EquivalentDataProperties',
        properties: [first, second, ...rest]
    };
}
/**
 * Create a DisjointDataProperties axiom.
 */
export function disjointDataProperties(first, second, ...rest) {
    return {
        axiomType: 'DisjointDataProperties',
        properties: [first, second, ...rest]
    };
}
/**
 * Create a DataPropertyDomain axiom.
 */
export function dataPropertyDomainAxiom(property, domain, annotations) {
    return {
        axiomType: 'DataPropertyDomain',
        property,
        domain,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a DataPropertyRange axiom.
 */
export function dataPropertyRangeAxiom(property, range, annotations) {
    return {
        axiomType: 'DataPropertyRange',
        property,
        range,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a FunctionalDataProperty axiom.
 */
export function functionalDataProperty(property, annotations) {
    return {
        axiomType: 'FunctionalDataProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a DatatypeDefinition axiom.
 */
export function datatypeDefinition(datatypeRef, dataRange, annotations) {
    return {
        axiomType: 'DatatypeDefinition',
        datatype: datatypeRef,
        dataRange,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a HasKey axiom.
 */
export function hasKey(classExpression, objectPropertyExpressions, dataPropertyExpressions, annotations) {
    return {
        axiomType: 'HasKey',
        classExpression,
        objectPropertyExpressions,
        dataPropertyExpressions,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a SameIndividual axiom.
 */
export function sameIndividual(first, second, ...rest) {
    return {
        axiomType: 'SameIndividual',
        individuals: [first, second, ...rest]
    };
}
/**
 * Create a DifferentIndividuals axiom.
 */
export function differentIndividuals(first, second, ...rest) {
    return {
        axiomType: 'DifferentIndividuals',
        individuals: [first, second, ...rest]
    };
}
/**
 * Create a ClassAssertion axiom.
 */
export function classAssertion(classExpression, individual, annotations) {
    return {
        axiomType: 'ClassAssertion',
        classExpression,
        individual,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an ObjectPropertyAssertion axiom.
 */
export function objectPropertyAssertion(property, sourceIndividual, targetIndividual, annotations) {
    return {
        axiomType: 'ObjectPropertyAssertion',
        property,
        sourceIndividual,
        targetIndividual,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a NegativeObjectPropertyAssertion axiom.
 */
export function negativeObjectPropertyAssertion(property, sourceIndividual, targetIndividual, annotations) {
    return {
        axiomType: 'NegativeObjectPropertyAssertion',
        property,
        sourceIndividual,
        targetIndividual,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a DataPropertyAssertion axiom.
 */
export function dataPropertyAssertion(property, sourceIndividual, targetValue, annotations) {
    return {
        axiomType: 'DataPropertyAssertion',
        property,
        sourceIndividual,
        targetValue,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a NegativeDataPropertyAssertion axiom.
 */
export function negativeDataPropertyAssertion(property, sourceIndividual, targetValue, annotations) {
    return {
        axiomType: 'NegativeDataPropertyAssertion',
        property,
        sourceIndividual,
        targetValue,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an AnnotationAssertion axiom.
 */
export function annotationAssertion(property, subject, value, annotations) {
    return {
        axiomType: 'AnnotationAssertion',
        property,
        subject,
        value,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create a SubAnnotationPropertyOf axiom.
 */
export function subAnnotationPropertyOf(subProperty, superProperty, annotations) {
    return {
        axiomType: 'SubAnnotationPropertyOf',
        subProperty,
        superProperty,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an AnnotationPropertyDomain axiom.
 */
export function annotationPropertyDomainAxiom(property, domain, annotations) {
    return {
        axiomType: 'AnnotationPropertyDomain',
        property,
        domain,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an AnnotationPropertyRange axiom.
 */
export function annotationPropertyRangeAxiom(property, range, annotations) {
    return {
        axiomType: 'AnnotationPropertyRange',
        property,
        range,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
